import { Show, createEffect } from 'solid-js';
import { Portal } from 'solid-js/web';
import { styled } from 'solid-styled-components';
import {
  COLORS,
  SPACING,
  BORDER_RADIUS,
  SHADOWS,
  Z_INDEX,
  ANIMATION,
  TYPOGRAPHY,
} from '../../../constants/design-tokens';
import GlyphControl from './GlyphControl';

// Styled components
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(11, 16, 9, 0.82);
  z-index: ${Z_INDEX.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACING[4]};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  transition: all ${ANIMATION.duration.base} ${ANIMATION.easing.easeInOut};
  border-radius: 0;
  overflow: hidden;
  /* Mobile: Remove padding for full-screen experience */
  @media (max-width: 768px) {
    padding: 0;
    border-radius: 0;
  }
`;

const ModalContainer = styled.div`
  background: ${COLORS.background.secondary};
  border-radius: ${BORDER_RADIUS.base};
  border: 1px solid ${COLORS.phosphor.greenMute};
  box-shadow: ${SHADOWS.lg};
  max-width: 90vw;
  max-height: 90vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  transform: ${(props) =>
    props.isVisible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)'};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: all ${ANIMATION.duration.base} ${ANIMATION.easing.easeInOut};
  overflow: hidden;
  /* Mobile: Full screen modal */
  @media (max-width: 768px) {
    max-width: 100vw;
    max-height: 100vh;
    max-height: 100dvh; /* Use dynamic viewport height for mobile */
    width: 100vw;
    height: 100vh;
    height: 100dvh; /* Use dynamic viewport height for mobile */
    border-radius: 0;
    transform: ${(props) => (props.isVisible ? 'translateY(0)' : 'translateY(100%)')};
  }
  /* Desktop: Constrain size */
  @media (min-width: 769px) {
    max-width: ${(props) => props.maxWidth || '80vw'};
    max-height: ${(props) => props.maxHeight || '80vh'};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING[4]} ${SPACING[6]};
  background: ${COLORS.background.dark};
  border-bottom: 1px solid ${COLORS.phosphor.greenMute};
  flex-shrink: 0;
  border-top-left-radius: ${BORDER_RADIUS.base};
  border-top-right-radius: ${BORDER_RADIUS.base};
  @media (max-width: 768px) {
    padding: ${SPACING[3]} ${SPACING[4]};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${COLORS.phosphor.green};
  font-family: ${TYPOGRAPHY.fontFamily.display.join(', ')};
  font-weight: ${TYPOGRAPHY.fontWeight.normal};
`;

const HeaderCluster = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING[4]};
`;

const ModalContent = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${SPACING[3]};
  padding: ${SPACING[4]} ${SPACING[6]};
  background: ${COLORS.background.dark};
  border-top: 1px solid ${COLORS.phosphor.greenMute};
  flex-shrink: 0;
  border-bottom-left-radius: ${BORDER_RADIUS.base};
  border-bottom-right-radius: ${BORDER_RADIUS.base};
  @media (max-width: 768px) {
    padding: ${SPACING[3]} ${SPACING[4]};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

/**
 * Professional Modal component with responsive behavior and accessibility
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Modal title
 * @param {JSX.Element} props.children - Modal content
 * @param {JSX.Element} props.footer - Custom footer content
 * @param {boolean} props.showCloseButton - Show close button in header
 * @param {boolean} props.closeOnBackdropClick - Close when clicking backdrop
 * @param {boolean} props.closeOnEscape - Close when pressing Escape key
 * @param {string} props.maxWidth - Maximum width for desktop
 * @param {string} props.maxHeight - Maximum height for desktop
 * @param {string} props.ariaLabel - Accessibility label
 */
const Modal = (props) => {
  const onClose = () => props.onClose?.();
  const showCloseButton = () => props.showCloseButton !== false;
  const closeOnBackdropClick = () => props.closeOnBackdropClick !== false;
  const closeOnEscape = () => props.closeOnEscape !== false;
  const isNarrow = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  const handleBackdropClick = (event) => {
    if (closeOnBackdropClick() && event.target === event.currentTarget) {
      onClose();
    }
  };

  createEffect(() => {
    if (!closeOnEscape() || !props.isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  createEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const restoreScroll = () => {
      const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      if (document.body.dataset.scrollY) {
        window.scrollTo(0, scrollY);
      }
      delete document.body.dataset.scrollY;
    };

    if (props.isOpen) {
      const scrollY = window.scrollY;

      if (isNarrow()) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'hidden';
      }

      document.body.dataset.scrollY = scrollY.toString();
    } else {
      restoreScroll();
    }

    return () => {
      if (props.isOpen) {
        restoreScroll();
      }
    };
  });

  return (
    <Show when={props.isOpen}>
      <Portal>
        <ModalBackdrop
          isVisible={props.isOpen}
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label={props.ariaLabel || props.title}
          data-testid={props['data-testid']}
        >
          <ModalContainer
            isVisible={props.isOpen}
            maxWidth={props.maxWidth}
            maxHeight={props.maxHeight}
            onClick={(e) => e.stopPropagation()}
          >
            <Show when={props.title || showCloseButton()}>
              <ModalHeader>
                <Show when={props.title}>
                  <ModalTitle>{props.title}</ModalTitle>
                </Show>
                <HeaderCluster>
                  {props.headerActions}
                  <Show when={showCloseButton()}>
                    <GlyphControl mark="x" onClick={onClose} ariaLabel="Close modal" title="esc" />
                  </Show>
                </HeaderCluster>
              </ModalHeader>
            </Show>

            <ModalContent>{props.children}</ModalContent>

            <Show when={props.footer}>
              <ModalFooter>{props.footer}</ModalFooter>
            </Show>
          </ModalContainer>
        </ModalBackdrop>
      </Portal>
    </Show>
  );
};

export default Modal;
